import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Users, MapPin, MessageSquare, ClipboardList, Plus, Loader2, X,
  Edit3, Trash2, Download, Search, RefreshCw, ChevronRight, AlertTriangle,
  Heart, Sparkles, ListChecks, HandHeart, Map as MapIcon, QrCode, Store,
  BookOpen, Building2,
} from 'lucide-react';
import OutreachMap from '../../../components/admin/OutreachMap';
import SurveySchemaEditor from '../../../components/admin/SurveySchemaEditor';
import VolunteerTokenManager from '../../../components/admin/VolunteerTokenManager';
import ZoneInsightsDrilldown from '../../../components/admin/ZoneInsightsDrilldown';
import LocationsPane from '../../../components/admin/LocationsPane';
import VolunteerGuideEditor from '../../../components/admin/VolunteerGuideEditor';
import CommunityInterestInsightsCard from '../../../components/admin/CommunityInterestInsightsCard';
import SegmentsPanel from '../../../components/admin/SegmentsPanel';
import PilotDashboard from '../../../components/admin/PilotDashboard';
import AdminPartnershipsTab from './AdminPartnershipsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Subtle, dignity-centered language — never use lead/prospect/target/beneficiary
const STATUS_META = {
  active: { label: 'Active', cls: 'bg-green-100 text-green-800 border-green-200' },
  paused: { label: 'Paused', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  archived: { label: 'Archived', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return '—'; }
};

const fmtFull = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

/* =====================================================================
 * Zone form modal
 * ===================================================================*/
const ZoneFormModal = ({ open, initial, onClose, onSave, onEditSurvey }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: '', neighborhood: '', city: '', state: '', description: '',
    color_tag: '', status: 'active', is_pilot: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        neighborhood: initial?.neighborhood || '',
        city: initial?.city || '',
        state: initial?.state || '',
        description: initial?.description || '',
        color_tag: initial?.color_tag || '',
        status: initial?.status || 'active',
        is_pilot: !!initial?.is_pilot,
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try { await onSave(form, initial?.id); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="zone-form-modal">
      <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-[#0A0A0A]">{isEdit ? 'Edit Zone' : 'New Outreach Zone'}</h3>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="zone-form-close"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-[11px] text-[#4B5563]">A zone is a neighborhood or area where the team is building relationships with neighbors. Streets and conversation logs are nested inside.</p>
        <div className="space-y-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zone name *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="East New York Pilot"
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
              data-testid="zone-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Neighborhood</label>
              <input value={form.neighborhood} onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
                placeholder="East New York"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="zone-neighborhood-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">City</label>
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Brooklyn"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="zone-city-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">State</label>
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                placeholder="NY" maxLength={4}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="zone-state-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="zone-status-select">
                {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              placeholder="Why this zone matters, who the partners are, any context for the team..."
              className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none resize-none"
              data-testid="zone-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zone color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.color_tag || '#BA1826'}
                  onChange={(e) => setForm({ ...form, color_tag: e.target.value })}
                  className="w-10 h-8 border border-[#E5E7EB] rounded cursor-pointer"
                  data-testid="zone-color-input" />
                <input type="text" value={form.color_tag}
                  onChange={(e) => setForm({ ...form, color_tag: e.target.value })}
                  placeholder="#BA1826"
                  className="flex-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-mono focus:border-[#BA1826] focus:outline-none"
                  data-testid="zone-color-hex-input" />
              </div>
              <p className="text-[10px] text-[#9CA3AF] mt-0.5">Used on the map + insights badges.</p>
            </div>
            {isEdit && onEditSurvey && (
              <button type="button" onClick={() => onEditSurvey(initial)}
                className="text-xs px-3 py-1.5 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center justify-center gap-1.5"
                data-testid="zone-edit-survey-btn">
                <ClipboardList className="w-3 h-3" /> Edit zone survey
              </button>
            )}
            <label className="flex items-center gap-2 text-xs text-[#0A0A0A] cursor-pointer bg-[#FFF8F8] border border-[#BA1826]/30 rounded px-2 py-1.5">
              <input type="checkbox" checked={form.is_pilot}
                onChange={(e) => setForm({ ...form, is_pilot: e.target.checked })}
                data-testid="zone-pilot-toggle" />
              <span className="flex-1">
                <strong>Pilot zone</strong>
                <span className="block text-[10px] text-[#4B5563]">Surfaces this zone on the Pilot dashboard. Multiple pilots are OK; most recent is the active hero.</span>
              </span>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.name.trim()}
            className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="zone-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Create zone'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =====================================================================
 * Quick-Log modal — mobile-first interaction logger
 * ===================================================================*/
const LogFormModal = ({ open, zones, initialZoneId, interactionLabels, interactionTypes, onClose, onSave, initial }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    zone_id: '', street_id: '', interaction_types: ['conversation'],
    neighbor_name: '', notes: '', logged_on_behalf_of: '',
    follow_up_needed: false, follow_up_date: '',
    donation_amount: '', supplies: '',
  });
  const [streets, setStreets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Resolve initial types from either the new multi-select field or the
      // legacy single field, so we round-trip older log rows cleanly.
      const initialTypes = initial?.interaction_types?.length
        ? initial.interaction_types
        : (initial?.interaction_type ? [initial.interaction_type] : ['conversation']);
      setForm({
        zone_id: initial?.zone_id || initialZoneId || (zones[0]?.id || ''),
        street_id: initial?.street_id || '',
        interaction_types: initialTypes,
        neighbor_name: initial?.neighbor_name || '',
        notes: initial?.notes || '',
        logged_on_behalf_of: initial?.logged_on_behalf_of || '',
        follow_up_needed: !!initial?.follow_up_needed,
        follow_up_date: initial?.follow_up_date || '',
        donation_amount: initial?.donation_amount ?? '',
        supplies: (initial?.supplies_requested_list || []).join(', '),
      });
    }
  }, [open, initial, initialZoneId, zones]);

  useEffect(() => {
    if (!open || !form.zone_id) { setStreets([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/zones/${form.zone_id}/streets`, { withCredentials: true });
        if (!cancelled) setStreets(r.data?.streets || []);
      } catch { setStreets([]); }
    })();
    return () => { cancelled = true; };
  }, [open, form.zone_id]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.zone_id || !form.interaction_types?.length) return;
    setSaving(true);
    try {
      const body = {
        zone_id: form.zone_id,
        street_id: form.street_id || null,
        interaction_types: form.interaction_types,
        // Keep the legacy single field populated with the first selection so
        // older list/export code paths render a primary type without changes.
        interaction_type: form.interaction_types[0],
        neighbor_name: form.neighbor_name.trim() || null,
        notes: form.notes.trim() || null,
        logged_on_behalf_of: form.logged_on_behalf_of.trim() || null,
        follow_up_needed: !!form.follow_up_needed,
        follow_up_date: form.follow_up_date || null,
        donation_amount: form.donation_amount === '' ? null : Number(form.donation_amount),
        supplies_requested_list: form.supplies.trim() ? form.supplies.split(',').map(s => s.trim()).filter(Boolean) : null,
      };
      await onSave(body, initial?.id);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="log-form-modal">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-5 space-y-3">
        <div className="flex items-center justify-between sticky top-0 bg-white pb-2 border-b border-[#E5E7EB] -mx-5 -mt-5 px-5 pt-5">
          <div>
            <h3 className="font-heading text-base font-bold text-[#0A0A0A] flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#BA1826]" />
              {isEdit ? 'Edit Interaction Log' : 'Log a Conversation'}
            </h3>
            <p className="text-[11px] text-[#4B5563] mt-0.5">Every meaningful interaction with a neighbor counts.</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-[#0A0A0A]" data-testid="log-form-close"><X className="w-4 h-4" /></button>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Zone *</label>
          <select value={form.zone_id} onChange={(e) => setForm({ ...form, zone_id: e.target.value, street_id: '' })}
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
            data-testid="log-zone-select">
            <option value="">— Select a zone —</option>
            {zones.map(z => <option key={z.id} value={z.id}>{z.name}{z.neighborhood ? ` · ${z.neighborhood}` : ''}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Street (optional)</label>
          <select value={form.street_id} onChange={(e) => setForm({ ...form, street_id: e.target.value })}
            disabled={!form.zone_id || streets.length === 0}
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none disabled:bg-[#F8F9FA]"
            data-testid="log-street-select">
            <option value="">— No specific street —</option>
            {streets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-1 block">
            Type of interaction *
            <span className="text-[9px] text-[#9CA3AF] font-normal normal-case ml-1">— pick all that apply</span>
          </label>
          <div className="grid grid-cols-2 gap-1.5" data-testid="log-interaction-chips">
            {interactionTypes.map(t => {
              const active = form.interaction_types.includes(t);
              const label = interactionLabels[t] || t;
              return (
                <button key={t} type="button"
                  onClick={() => setForm((prev) => {
                    const set = new Set(prev.interaction_types);
                    if (set.has(t)) set.delete(t); else set.add(t);
                    // Keep at least one type selected (UI invariant).
                    const next = set.size ? Array.from(set) : prev.interaction_types;
                    return { ...prev, interaction_types: next };
                  })}
                  className={`text-[11px] py-1.5 px-2 rounded border text-left transition-all flex items-center gap-1.5 ${
                    active ? 'bg-[#BA1826] text-white border-[#BA1826] font-semibold' : 'bg-white border-[#E5E7EB] text-[#4B5563] hover:border-[#BA1826]'
                  }`}
                  data-testid={`log-itype-${t}`}>
                  <span className={`w-3 h-3 rounded-sm border flex items-center justify-center text-[8px] font-bold shrink-0 ${
                    active ? 'bg-white text-[#BA1826] border-white' : 'border-[#9CA3AF]'
                  }`}>
                    {active ? '✓' : ''}
                  </span>
                  {label}
                </button>
              );
            })}
          </div>
          {form.interaction_types.length > 1 && (
            <p className="text-[10px] text-[#0A0A0A] mt-1">
              <strong>{form.interaction_types.length}</strong> interaction types selected.
            </p>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Neighbor name (optional)</label>
          <input value={form.neighbor_name} onChange={(e) => setForm({ ...form, neighbor_name: e.target.value })}
            placeholder="Leave blank if neighbor preferred not to share"
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
            data-testid="log-neighbor-name-input" />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Notes</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            placeholder="What did you talk about? Anything to remember for next time?"
            className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none resize-none"
            data-testid="log-notes-input" />
        </div>

        <details className="border border-[#E5E7EB] rounded">
          <summary className="text-[11px] font-semibold text-[#4B5563] px-3 py-2 cursor-pointer">More details (optional)</summary>
          <div className="p-3 space-y-2 border-t border-[#E5E7EB]">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Logged on behalf of</label>
              <input value={form.logged_on_behalf_of} onChange={(e) => setForm({ ...form, logged_on_behalf_of: e.target.value })}
                placeholder="Volunteer's name if you're entering this for them"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="log-on-behalf-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-start gap-2 cursor-pointer text-xs text-[#0A0A0A] mt-2">
                <input type="checkbox" checked={form.follow_up_needed}
                  onChange={(e) => setForm({ ...form, follow_up_needed: e.target.checked })}
                  className="mt-0.5"
                  data-testid="log-followup-checkbox" />
                <span>Follow-up needed</span>
              </label>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Follow-up date</label>
                <input type="date" value={form.follow_up_date}
                  onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                  className="w-full px-2.5 py-1 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                  data-testid="log-followup-date-input" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Supplies requested (comma-separated)</label>
              <input value={form.supplies} onChange={(e) => setForm({ ...form, supplies: e.target.value })}
                placeholder="e.g. baby formula, hygiene kit, food bucket"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="log-supplies-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">Donation amount ($, if any)</label>
              <input type="number" min="0" step="0.01" value={form.donation_amount}
                onChange={(e) => setForm({ ...form, donation_amount: e.target.value })}
                placeholder="Only if a neighbor gave directly during the visit"
                className="w-full px-2.5 py-1.5 border border-[#E5E7EB] rounded text-sm focus:border-[#BA1826] focus:outline-none"
                data-testid="log-donation-input" />
            </div>
          </div>
        </details>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
          <button onClick={onClose}
            className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded">Cancel</button>
          <button onClick={handleSave} disabled={saving || !form.zone_id}
            className="text-xs px-4 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="log-save-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Heart className="w-3 h-3" />}
            {isEdit ? 'Save changes' : 'Save log'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* =====================================================================
 * Streets sub-panel — shown when a zone is expanded
 * ===================================================================*/
const StreetsPanel = ({ zoneId, canWrite }) => {
  const [streets, setStreets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/zones/${zoneId}/streets`, { withCredentials: true });
      setStreets(r.data?.streets || []);
    } catch { setStreets([]); }
    finally { setLoading(false); }
  }, [zoneId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/outreach/zones/${zoneId}/streets`, { name: newName.trim() }, { withCredentials: true });
      setNewName(''); await load();
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this street? Existing logs that reference it will keep their notes.')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/streets/${id}`, { withCredentials: true });
    await load();
  };

  return (
    <div className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-3 mt-2" data-testid={`streets-panel-${zoneId}`}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">Streets in this zone</p>
      {canWrite && (
        <div className="flex items-center gap-1.5 mb-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="Add a street (e.g. Atlantic Ave)"
            className="flex-1 px-2.5 py-1 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
            data-testid={`street-name-input-${zoneId}`} />
          <button onClick={add} disabled={adding || !newName.trim()}
            className="text-[11px] px-2 py-1 bg-[#0A0A0A] text-white rounded hover:bg-[#333] disabled:opacity-50 inline-flex items-center gap-1"
            data-testid={`street-add-btn-${zoneId}`}>
            {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
          </button>
        </div>
      )}
      {loading ? <Loader2 className="w-3 h-3 animate-spin text-[#BA1826]" /> :
        streets.length === 0 ? (
          <p className="text-[11px] text-[#9CA3AF]">No streets yet. Add the first one — or skip this and just log conversations at the zone level.</p>
        ) : (
          <ul className="space-y-2">
            {streets.map(s => (
              <li key={s.id} className="text-xs bg-white border border-[#E5E7EB] rounded p-2" data-testid={`street-row-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0A0A0A] inline-flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold">{s.name}</span>
                      {s.is_pilot && (
                        <span className="text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded bg-[#FFF8F8] text-[#BA1826] border-[#BA1826]/30 inline-flex items-center gap-1"
                          data-testid={`street-pilot-badge-${s.id}`}>
                          <Sparkles className="w-2.5 h-2.5" /> Pilot
                        </span>
                      )}
                      {s.notes && <span className="text-[#9CA3AF] text-[11px]">· {s.notes}</span>}
                    </p>
                  </div>
                  {canWrite && (
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] text-[#4B5563] inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!s.is_pilot}
                          onChange={async (e) => {
                            await axios.put(`${BACKEND_URL}/api/admin/outreach/streets/${s.id}`,
                              { is_pilot: e.target.checked }, { withCredentials: true });
                            await load();
                          }}
                          data-testid={`street-pilot-toggle-${s.id}`} />
                        Pilot
                      </label>
                      <button onClick={() => remove(s.id)} className="text-red-600 hover:text-red-800" data-testid={`street-delete-${s.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                {/* Segments under this street */}
                <SegmentsPanel streetId={s.id} canWrite={canWrite} />
              </li>
            ))}
          </ul>
        )
      }
    </div>
  );
};

/* =====================================================================
 * Main Outreach tab
 * ===================================================================*/
const AdminOutreachTab = ({ canWrite = true }) => {
  const [subtab, setSubtab] = useState('partnerships'); // 'partnerships' | 'logs' | 'zones' | 'locations' | 'map' | 'pilot' | 'guide' | 'insights'
  const [zonesData, setZonesData] = useState({ zones: [], statuses: [], interaction_labels: {} });
  const [logsData, setLogsData] = useState({ logs: [], zone_map: {}, street_map: {}, interaction_labels: {}, interaction_types: [], insights_order: [] });
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ zone_id: '', interaction_type: '', search: '', follow_up_only: false });
  const [editingZone, setEditingZone] = useState(null);
  const [showZoneForm, setShowZoneForm] = useState(false);
  const [showLogForm, setShowLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [expandedZoneId, setExpandedZoneId] = useState(null);
  const [surveyEditor, setSurveyEditor] = useState(null); // null | {zoneId, zoneName} | {global: true}
  const [tokenManagerZone, setTokenManagerZone] = useState(null);
  const [drilldownZone, setDrilldownZone] = useState(null);
  const [insightsRange, setInsightsRange] = useState({ days: 0 }); // 0 = all-time
  const [mapLocations, setMapLocations] = useState([]); // for map pins

  const reloadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const insightsParams = new URLSearchParams();
      if (insightsRange.days && insightsRange.days > 0) {
        const start = new Date();
        start.setDate(start.getDate() - insightsRange.days);
        insightsParams.set('date_from', start.toISOString().slice(0, 10));
      }
      const [zRes, lRes, iRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/outreach/zones`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/outreach/logs?${new URLSearchParams(
          Object.entries(filters).reduce((acc, [k, v]) => { if (v && v !== false) acc[k] = String(v); return acc; }, {})
        ).toString()}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/outreach/insights?${insightsParams.toString()}`, { withCredentials: true }),
      ]);
      setZonesData(zRes.data);
      setLogsData(lRes.data);
      setInsights(iRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load outreach data');
    } finally { setLoading(false); }
  }, [filters, insightsRange]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const saveZone = async (form, id) => {
    if (id) await axios.put(`${BACKEND_URL}/api/admin/outreach/zones/${id}`, form, { withCredentials: true });
    else await axios.post(`${BACKEND_URL}/api/admin/outreach/zones`, form, { withCredentials: true });
    await reloadAll();
  };

  const deleteZone = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.name}"? This will remove its streets and all interaction logs for this zone.`)) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/zones/${zone.id}`, { withCredentials: true });
    await reloadAll();
  };

  const saveLog = async (body, id) => {
    if (id) await axios.put(`${BACKEND_URL}/api/admin/outreach/logs/${id}`, body, { withCredentials: true });
    else await axios.post(`${BACKEND_URL}/api/admin/outreach/logs`, body, { withCredentials: true });
    await reloadAll();
  };

  const deleteLog = async (logId) => {
    if (!window.confirm('Remove this interaction log?')) return;
    await axios.delete(`${BACKEND_URL}/api/admin/outreach/logs/${logId}`, { withCredentials: true });
    await reloadAll();
  };

  const saveZoneGeo = async (zoneId, coords) => {
    await axios.put(`${BACKEND_URL}/api/admin/outreach/zones/${zoneId}/geo`, coords, { withCredentials: true });
    await reloadAll();
  };

  const openLogFromMap = (zoneId) => {
    setFilters((f) => ({ ...f, zone_id: zoneId }));
    setSubtab('logs');
    setEditingLog(null);
    setShowLogForm(true);
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (filters.zone_id) params.set('zone_id', filters.zone_id);
    window.location.href = `${BACKEND_URL}/api/admin/outreach/logs/export.csv?${params}`;
  };

  // Fetch locations for the map once when the Map sub-tab is opened
  useEffect(() => {
    if (subtab !== 'map') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/locations`, { withCredentials: true });
        if (!cancelled) setMapLocations(r.data?.locations || []);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [subtab]);

  // Doctrine-ordered insights icons
  const insightIcons = useMemo(() => ({
    conversation: MessageSquare,
    survey_collected: ClipboardList,
    interested_in_updates: Sparkles,
    follow_up_requested: ListChecks,
    supplies_requested: HandHeart,
    flyer_dropped: Download,
    no_answer: AlertTriangle,
    other: Edit3,
    donation_received: Heart,
  }), []);

  return (
    <div className="space-y-5" data-testid="admin-outreach-tab">
      <div>
        <h2 className="font-heading text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
          <Users className="w-5 h-5 text-[#BA1826]" />
          Community Outreach
        </h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Log conversations with neighbors, track follow-ups, and grow real relationships in the neighborhoods you serve. Every survey, every check-in, every <em>"yes, come back next week"</em> is a win — donations are one part of a much bigger picture.
        </p>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E7EB] flex-wrap" data-testid="outreach-subtabs">
        {[
          { id: 'partnerships', label: 'Partnerships', Icon: Building2 },
          { id: 'logs', label: 'Interaction Logs', Icon: MessageSquare },
          { id: 'zones', label: 'Zones & Streets', Icon: MapPin },
          { id: 'locations', label: 'Locations', Icon: Store },
          { id: 'map', label: 'Map', Icon: MapIcon },
          { id: 'pilot', label: 'Pilot', Icon: Sparkles },
          { id: 'guide', label: 'Volunteer Guide', Icon: BookOpen },
          { id: 'insights', label: 'Insights', Icon: ListChecks },
        ].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setSubtab(id)}
            className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 border-b-2 transition-all ${
              subtab === id ? 'border-[#BA1826] text-[#BA1826]' : 'border-transparent text-[#4B5563] hover:text-[#0A0A0A]'
            }`}
            data-testid={`outreach-subtab-${id}`}>
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        ))}
        <div className="flex-1" />
        {canWrite && (
          <button onClick={() => setSurveyEditor({ global: true })}
            className="text-[11px] px-2 py-1 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center gap-1"
            data-testid="outreach-global-survey-btn">
            <ClipboardList className="w-3 h-3" /> Global survey
          </button>
        )}
        <button onClick={reloadAll} disabled={loading}
          className="text-[11px] px-2 py-1 text-[#4B5563] hover:text-[#0A0A0A] inline-flex items-center gap-1"
          data-testid="outreach-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">{error}</div>}

      {/* ============ PARTNERSHIPS ============ */}
      {subtab === 'partnerships' && (
        <AdminPartnershipsTab canWrite={canWrite} />
      )}

      {/* ============ LOGS ============ */}
      {subtab === 'logs' && (
        <div className="space-y-3" data-testid="outreach-logs-pane">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input type="text" placeholder="Search neighbor name or notes..." value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-8 pr-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
                data-testid="logs-search-input" />
            </div>
            <select value={filters.zone_id} onChange={(e) => setFilters({ ...filters, zone_id: e.target.value })}
              className="px-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
              data-testid="logs-zone-filter">
              <option value="">All Zones</option>
              {zonesData.zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
            <select value={filters.interaction_type} onChange={(e) => setFilters({ ...filters, interaction_type: e.target.value })}
              className="px-3 py-2 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none"
              data-testid="logs-type-filter">
              <option value="">All Interactions</option>
              {(logsData.interaction_types || []).map(t => <option key={t} value={t}>{logsData.interaction_labels[t] || t}</option>)}
            </select>
            <label className="text-xs text-[#4B5563] inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={filters.follow_up_only}
                onChange={(e) => setFilters({ ...filters, follow_up_only: e.target.checked })}
                data-testid="logs-followup-only" />
              Follow-ups only
            </label>
            <button onClick={exportCsv}
              className="text-xs px-3 py-2 border border-[#E5E7EB] rounded hover:bg-[#F8F9FA] inline-flex items-center gap-1"
              data-testid="logs-export-btn">
              <Download className="w-3 h-3" /> Export CSV
            </button>
            {canWrite && (
              <button onClick={() => { setEditingLog(null); setShowLogForm(true); }}
                className="text-xs px-3 py-2 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1"
                data-testid="logs-new-btn">
                <Heart className="w-3 h-3" /> Log a Conversation
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : logsData.logs.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="logs-empty">
              <Heart className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
              <p className="text-sm text-[#4B5563]">No interaction logs yet — let&apos;s log the first conversation with a neighbor.</p>
              {canWrite && (
                <button onClick={() => { setEditingLog(null); setShowLogForm(true); }}
                  className="mt-3 text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Start logging
                </button>
              )}
            </div>
          ) : (
            <div className="border border-[#E5E7EB] rounded-lg overflow-hidden bg-white">
              <table className="w-full text-xs" data-testid="logs-table">
                <thead className="bg-[#F8F9FA] border-b border-[#E5E7EB]">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#4B5563]">
                    <th className="px-3 py-2 font-bold">When</th>
                    <th className="px-3 py-2 font-bold">Zone</th>
                    <th className="px-3 py-2 font-bold">Interaction</th>
                    <th className="px-3 py-2 font-bold">Neighbor</th>
                    <th className="px-3 py-2 font-bold">Notes</th>
                    <th className="px-3 py-2 font-bold">Volunteer</th>
                    <th className="px-3 py-2 font-bold">Follow-up</th>
                    {canWrite && <th className="px-3 py-2"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {logsData.logs.map((r) => {
                    const zone = logsData.zone_map[r.zone_id];
                    const street = logsData.street_map[r.street_id];
                    return (
                      <tr key={r.id} className="hover:bg-[#F8F9FA]" data-testid={`log-row-${r.id}`}>
                        <td className="px-3 py-2 text-[#4B5563] whitespace-nowrap" title={fmtFull(r.created_at)}>{fmtDate(r.created_at)}</td>
                        <td className="px-3 py-2 text-[#0A0A0A]">
                          {zone?.name || <em className="text-[#9CA3AF]">(zone removed)</em>}
                          {street ? <span className="text-[#9CA3AF] block text-[10px]">{street.name}</span> : null}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {((r.interaction_types && r.interaction_types.length)
                              ? r.interaction_types
                              : [r.interaction_type]
                            ).filter(Boolean).map((t) => (
                              <span key={t} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#F1F5F9] text-[#0A0A0A] border border-[#E5E7EB] px-1.5 py-0.5 rounded">
                                {logsData.interaction_labels[t] || t}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[#0A0A0A]">{r.neighbor_name || <em className="text-[#9CA3AF]">—</em>}</td>
                        <td className="px-3 py-2 text-[#4B5563] max-w-[260px] truncate" title={r.notes || ''}>{r.notes || ''}</td>
                        <td className="px-3 py-2 text-[#4B5563]">
                          {r.volunteer_name || '—'}
                          {r.logged_on_behalf_of ? <span className="block text-[10px] text-[#9CA3AF]">on behalf of {r.logged_on_behalf_of}</span> : null}
                        </td>
                        <td className="px-3 py-2 text-[#4B5563]">
                          {r.follow_up_needed ? (
                            <span className="text-amber-700 font-semibold">Yes{r.follow_up_date ? ` · ${r.follow_up_date}` : ''}</span>
                          ) : '—'}
                        </td>
                        {canWrite && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button onClick={() => { setEditingLog(r); setShowLogForm(true); }}
                              className="text-[#4B5563] hover:text-[#0A0A0A] mr-2" data-testid={`log-edit-${r.id}`}>
                              <Edit3 className="w-3 h-3" />
                            </button>
                            <button onClick={() => deleteLog(r.id)} className="text-red-600 hover:text-red-800" data-testid={`log-delete-${r.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ============ ZONES ============ */}
      {subtab === 'zones' && (
        <div className="space-y-3" data-testid="outreach-zones-pane">
          {canWrite && (
            <div className="flex justify-end">
              <button onClick={() => { setEditingZone(null); setShowZoneForm(true); }}
                className="text-xs px-3 py-2 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] inline-flex items-center gap-1"
                data-testid="zone-new-btn">
                <Plus className="w-3 h-3" /> New Zone
              </button>
            </div>
          )}
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : zonesData.zones.length === 0 ? (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="zones-empty">
              <MapPin className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
              <p className="text-sm text-[#4B5563]">No zones yet. Start with a single neighborhood pilot and grow from there.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {zonesData.zones.map(z => {
                const sMeta = STATUS_META[z.status] || STATUS_META.active;
                const expanded = expandedZoneId === z.id;
                return (
                  <div key={z.id} className="bg-white border border-[#E5E7EB] rounded-lg" data-testid={`zone-card-${z.id}`}>
                    <div className="flex items-center justify-between gap-2 p-3">
                      <button onClick={() => setExpandedZoneId(expanded ? null : z.id)}
                        className="flex-1 flex items-center gap-2 text-left"
                        data-testid={`zone-toggle-${z.id}`}>
                        <ChevronRight className={`w-4 h-4 text-[#9CA3AF] transition-transform ${expanded ? 'rotate-90' : ''}`} />
                        <div>
                          <p className="font-bold text-sm text-[#0A0A0A] inline-flex items-center gap-2 flex-wrap">
                            {z.name}
                            <span className={`text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded ${sMeta.cls}`}>{sMeta.label}</span>
                            {z.is_pilot && (
                              <span className="text-[9px] font-bold uppercase tracking-wider border px-1.5 py-0.5 rounded bg-[#FFF8F8] text-[#BA1826] border-[#BA1826]/30 inline-flex items-center gap-1"
                                data-testid={`zone-pilot-badge-${z.id}`}>
                                <Sparkles className="w-2.5 h-2.5" /> Pilot
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-[#4B5563]">
                            {[z.neighborhood, z.city, z.state].filter(Boolean).join(' · ') || 'No location set'}
                            {' · '}
                            <span className="text-[#9CA3AF]">{z.log_count} log{z.log_count === 1 ? '' : 's'}</span>
                          </p>
                        </div>
                      </button>
                      {canWrite && (
                        <div className="flex items-center gap-1">
                          <button onClick={() => setTokenManagerZone(z)}
                            className="p-1.5 text-[#4B5563] hover:text-[#BA1826] hover:bg-[#F8F9FA] rounded"
                            title="Volunteer QR & tokens"
                            data-testid={`zone-qr-${z.id}`}>
                            <QrCode className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => { setEditingZone(z); setShowZoneForm(true); }}
                            className="p-1.5 text-[#4B5563] hover:text-[#0A0A0A] hover:bg-[#F8F9FA] rounded"
                            data-testid={`zone-edit-${z.id}`}>
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => deleteZone(z)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            data-testid={`zone-delete-${z.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {expanded && (
                      <div className="px-3 pb-3">
                        {z.description && <p className="text-xs text-[#4B5563] mb-2 italic">"{z.description}"</p>}
                        <StreetsPanel zoneId={z.id} canWrite={canWrite} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ LOCATIONS ============ */}
      {subtab === 'locations' && (
        <LocationsPane canWrite={canWrite} zones={zonesData.zones || []} />
      )}

      {/* ============ MAP ============ */}
      {subtab === 'map' && (
        <OutreachMap
          zones={zonesData.zones || []}
          locations={mapLocations}
          canWrite={canWrite}
          onSaveGeo={saveZoneGeo}
          onOpenLogForm={openLogFromMap}
        />
      )}

      {/* ============ VOLUNTEER GUIDE ============ */}
      {subtab === 'guide' && (
        <VolunteerGuideEditor canWrite={canWrite} />
      )}

      {/* ============ PILOT DASHBOARD ============ */}
      {subtab === 'pilot' && (
        <PilotDashboard canWrite={canWrite} />
      )}

      {/* ============ INSIGHTS ============ */}
      {subtab === 'insights' && (
        <div className="space-y-3" data-testid="outreach-insights-pane">
          {/* Date range filter */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mr-1">Range</span>
            {[
              { d: 0, l: 'All time' },
              { d: 7, l: 'Last 7d' },
              { d: 30, l: 'Last 30d' },
              { d: 90, l: 'Last 90d' },
              { d: 365, l: 'Last year' },
            ].map(({ d, l }) => (
              <button key={d} onClick={() => setInsightsRange({ days: d })}
                className={`text-[11px] px-2 py-0.5 rounded border ${
                  insightsRange.days === d ? 'bg-[#BA1826] text-white border-[#BA1826]' : 'bg-white border-[#E5E7EB] text-[#4B5563]'
                }`}
                data-testid={`insights-range-${d}`}>
                {l}
              </button>
            ))}
          </div>

          {!insights ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
          ) : (
            <>
              <div className="bg-gradient-to-r from-[#FFF8F8] to-white border border-[#BA1826]/30 rounded-lg p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-[#BA1826] mb-1">Relationships built</p>
                <p className="text-3xl font-heading font-bold text-[#0A0A0A]" data-testid="insights-total-logs">{insights.total_logs}</p>
                <p className="text-xs text-[#4B5563] mt-1">interactions logged across all zones — every one of them is a step closer to a neighbor.</p>
                {insights.follow_up_pending > 0 ? (
                  <p className="text-xs text-amber-700 mt-2" data-testid="insights-followup-pending">
                    <strong>{insights.follow_up_pending}</strong> follow-up{insights.follow_up_pending === 1 ? '' : 's'} pending — neighbors waiting to hear back from us.
                  </p>
                ) : (
                  <p className="text-xs text-[#9CA3AF] mt-2" data-testid="insights-followup-pending">
                    No follow-ups pending right now.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="insights-by-type">
                {(insights.by_type || []).map((row) => {
                  const Ico = insightIcons[row.interaction_type] || Edit3;
                  const isDonation = row.interaction_type === 'donation_received';
                  return (
                    <div key={row.interaction_type}
                      className={`bg-white border rounded-lg p-3 ${isDonation ? 'border-green-300 bg-green-50' : 'border-[#E5E7EB]'}`}
                      data-testid={`insight-${row.interaction_type}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563]">{row.label}</span>
                        <Ico className={`w-3.5 h-3.5 ${isDonation ? 'text-green-600' : 'text-[#BA1826]'}`} />
                      </div>
                      <p className={`text-xl font-heading font-bold ${isDonation ? 'text-green-700' : 'text-[#0A0A0A]'}`}>{row.count}</p>
                      {isDonation && insights.total_donations_amount > 0 && (
                        <p className="text-[10px] text-green-700 mt-0.5">${insights.total_donations_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} received</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-[#9CA3AF] italic">
                Doctrine note: conversations, surveys, and ongoing interest are listed first because that is where real community is built. Donations are one of many possible outcomes — never the only goal.
              </p>
              {/* Top requested programs (Community Interest Survey aggregation) */}
              <CommunityInterestInsightsCard days={insightsRange.days} />
              {/* Per-zone drilldown */}
              {zonesData.zones.length > 0 && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-3" data-testid="insights-zone-list">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#4B5563] mb-2">Drill into a zone</p>
                  <ul className="space-y-1">
                    {zonesData.zones.map((z) => (
                      <li key={z.id}>
                        <button onClick={() => setDrilldownZone({ id: z.id, name: z.name })}
                          className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-[#F8F9FA]"
                          data-testid={`insights-drill-${z.id}`}>
                          <span className="w-3 h-3 rounded-full inline-block" style={{ background: z.color_tag || '#BA1826' }} />
                          <span className="text-xs text-[#0A0A0A] flex-1">{z.name}</span>
                          <span className="text-[10px] text-[#9CA3AF]">{z.log_count} log{z.log_count === 1 ? '' : 's'}</span>
                          <ChevronRight className="w-3 h-3 text-[#9CA3AF]" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Floating quick-log button (mobile-friendly) on Logs sub-tab */}
      {subtab === 'logs' && canWrite && logsData.logs.length > 0 && (
        <button
          onClick={() => { setEditingLog(null); setShowLogForm(true); }}
          className="lg:hidden fixed bottom-4 right-4 z-30 bg-[#BA1826] text-white rounded-full w-12 h-12 shadow-lg flex items-center justify-center hover:bg-[#9F1421]"
          aria-label="Log a conversation"
          data-testid="logs-fab">
          <Heart className="w-5 h-5" />
        </button>
      )}

      {/* Modals */}
      <ZoneFormModal open={showZoneForm} initial={editingZone}
        onClose={() => { setShowZoneForm(false); setEditingZone(null); }}
        onSave={saveZone}
        onEditSurvey={(zone) => setSurveyEditor({ zoneId: zone.id, zoneName: zone.name })} />
      <LogFormModal open={showLogForm} zones={zonesData.zones}
        initialZoneId={filters.zone_id || null}
        interactionLabels={logsData.interaction_labels || {}}
        interactionTypes={logsData.interaction_types || []}
        initial={editingLog}
        onClose={() => { setShowLogForm(false); setEditingLog(null); }}
        onSave={saveLog} />
      <SurveySchemaEditor
        open={!!surveyEditor}
        zoneId={surveyEditor && !surveyEditor.global ? surveyEditor.zoneId : null}
        zoneName={surveyEditor && !surveyEditor.global ? surveyEditor.zoneName : null}
        onClose={() => setSurveyEditor(null)}
        onSaved={reloadAll}
      />
      <VolunteerTokenManager
        open={!!tokenManagerZone}
        zone={tokenManagerZone}
        canWrite={canWrite}
        onClose={() => setTokenManagerZone(null)}
      />
      <ZoneInsightsDrilldown
        open={!!drilldownZone}
        zoneId={drilldownZone?.id}
        zoneName={drilldownZone?.name}
        onClose={() => setDrilldownZone(null)}
      />
    </div>
  );
};

export default AdminOutreachTab;
